import { get, post, put } from '../utils/request';
import { credentials } from './user';

export const handleFetchProviderList = (user, { archived = false } = {}) => async (dispatch) => {
  try {
    const path = archived ? '/provider?archived=true' : '/provider';
    const response = await get(path, dispatch, credentials(user));
    const body = await response.json();

    const { providers } = body;

    dispatch(setProviderList(providers, body.archived_count));

    return true;
  } catch (error) {
    return false;
  }
};

// La liste ENTIÈRE — actifs et archivés — sans toucher au magasin. Deux raisons, et
// aucune n'est optionnelle :
//
//   • le doublon qu'on cherche à faire disparaître est souvent celui qu'on a déjà rangé
//     hors de vue, l'archivage étant le seul ménage possible jusqu'ici (`archived=all`) ;
//   • l'écran derrière la fenêtre affiche peut-être le résultat d'une recherche, ou sa
//     liste d'archivés : reverser celle-ci dans le magasin la remplacerait sous les yeux
//     de l'entrepreneur pendant qu'il choisit.
export const fetchProvidersList = (user) => async (dispatch) => {
  try {
    const response = await get('/provider?archived=all', dispatch, credentials(user));
    const body = await response.json();
    return body.providers || [];
  } catch (error) {
    return [];
  }
};

// Ce que la fusion laissera derrière elle, demandé AVANT le geste : les descriptions que
// les deux catalogues portent déjà. Le fournisseur fait partie de l'identité d'un item
// (description + fournisseur) ; réunir deux fiches peut donc laisser côte à côte deux
// items de même description à deux prix. Aucun n'est perdu, mais il faut le dire.
export const fetchProviderMergePreview = (user, targetId, sourceId) => async (dispatch) => {
  try {
    const response = await get(`/provider/${targetId}/merge_preview?source_id=${sourceId}`, dispatch, credentials(user));
    const body = await response.json();
    if (!response.ok) return [];
    return body.shared_descriptions || [];
  } catch (error) {
    return [];
  }
};

// `targetId` est la fiche CONSERVÉE, `sourceId` celle qui disparaît.
//
// La réponse ne rapporte pas la liste et on n'en redispatche pas : l'écran appelant a son
// propre filtre (actifs / archivés) et sa propre recherche, il la redemande lui-même.
export const handleMergeProviders = (targetId, sourceId, user) => async (dispatch) => {
  try {
    const response = await post(`/provider/${targetId}/merge`, { source_id: sourceId }, dispatch, credentials(user));
    const body = await response.json();
    if (!response.ok) return { error: body.error || 'Une erreur est survenue' };
    return { provider: body.provider, merged: body.merged };
  } catch (error) {
    return { error: 'Une erreur est survenue' };
  }
};

// Archive (active=false) ou réactive (active=true) un fournisseur.
export const handleArchiveProvider = (provider, active, user) => async (dispatch) => {
  try {
    const response = await put(`/provider/${provider.id}/archive`, { active }, dispatch, credentials(user));
    const body = await response.json();
    if (!response.ok) return false;
    dispatch(setProviderList(body.providers, body.archived_count));
    return true;
  } catch (error) {
    return false;
  }
};

export const handleCreateNewProviderList = (params, user) => async (dispatch) => {
  try {
    const response = await post('/provider', params, dispatch, credentials(user));
    const body = await response.json();
    if (!response.ok) return { error: body.error || 'Une erreur est survenue' };
    dispatch(setProviderList(body.providers));
    return body.provider;
  } catch (error) {
    return { error: 'Une erreur est survenue' };
  }
};

export const handleDeleteProviderList = (idsToDelete, user) => async (dispatch) => {
  try {
    const params = {
      ids: idsToDelete,
    };

    const response = await post('/delete_provider', params, dispatch, credentials(user));
    const body = await response.json();

    if (!response.ok) return { ok: false, archived: [], deleted: [] };

    dispatch(setProviderList(body.providers, body.archived_count));

    return { ok: true, archived: body.archived || [], deleted: body.deleted || [] };
  } catch (error) {
    return { ok: false, archived: [], deleted: [] };
  }
};

export const handleUpdateProviderList = (params, providerId, user) => async (dispatch) => {
  try {
    const response = await put(`/provider/${providerId}`, params, dispatch, credentials(user));
    const body = await response.json();
    // Conflit de verrou optimiste : la fiche a bougé en base entre-temps. On
    // rafraîchit la liste avec la version fraîche et on signale le conflit au modal.
    if (response.status === 409) {
      dispatch(setProviderList(body.providers));
      return { conflict: true, message: body.message, fresh: body.provider };
    }
    if (!response.ok) return { error: body.error || 'Une erreur est survenue' };
    dispatch(setProviderList(body.providers));
    return body.provider;
  } catch (error) {
    return { error: 'Une erreur est survenue' };
  }
};

export const handleFetchProvider = (user, id) => async () => {
  try {
    const response = await get(`/provider/${id}`, null, credentials(user));
    const body = await response.json();
    return body.provider || null;
  } catch {
    return null;
  }
};

export const handleFetchProviderProjects = (user, id) => async () => {
  try {
    const response = await get(`/provider/${id}/projects`, null, credentials(user));
    const body = await response.json();
    return { projects: body.projects || [], summary: body.summary || {} };
  } catch {
    return { projects: [], summary: {} };
  }
};

export const setProviderList = (providerList, archivedCount) => ({
  type: 'SET_PROVIDER_LIST',
  providerList,
  archivedCount,
});
